import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import {
  LocationCardAddress,
  LocationCardContainer,
  LocationCardContent,
  LocationCardHeader,
  LocationCardHeaderIcon,
  LocationCardMapIcon,
  LocationCardTitle,
} from "@babackapp/e-commerce-components/location-card";
import useSetReturnToStoreActivation from "@babackapp/private-api-client/client/use-set-return-to-store-activation";
import { ShopReturnToStoreStoresDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { ReturnToStoreFormValues } from "../shop-informations-settings-form/types";
import AddStoreDialog from "./add-to-store-dialog";
import ReturnToStoreForm from "./return-to-store-form";
import UpdateStoreDialog from "./update-store-dialog";
import { HomeIcon } from "lucide-react";
import StoreOpeningState from "@babackapp/e-commerce-components/store-opening-state";
import { Badge } from "@babackapp/ui/components/badge";

export default function ReturnToStoresCard() {
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: setReturnToStoreActivation } =
    useSetReturnToStoreActivation(shop.id);

  const onSubmit = (values: ReturnToStoreFormValues) => {
    return setReturnToStoreActivation(values.returnToStoreActivated);
  };

  if (!shop) return null;

  return (
    <Card>
      <CardContent className="mt-4 flex-col flex gap-4 px-0">
        <ReturnToStoreForm
          onSubmit={onSubmit}
          defaultValues={{
            returnToStoreActivated: shop.returnToStore.activated,
          }}
        />
        <ReturnToStoreList />
      </CardContent>
    </Card>
  );
}

function ReturnToStoreList() {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  const hasAdminRights = useViewerHasAtLeastAdminRights();

  if (!shop.plan.returnToStore) {
    return null;
  }

  return (
    <div className="space-y-2 px-4">
      <div className="space-y-2 mb-2">
        <div role="list" className="flex flex-col gap-2">
          {shop.returnToStore.stores.map(
            (store: ShopReturnToStoreStoresDto) => (
              <StoreItem key={store.id} store={store} />
            ),
          )}
        </div>
      </div>
      {hasAdminRights && (
        <AddStoreDialog>
          <Button variant="secondary" size="sm">
            {t("settings.shop.physic.addStore")}
          </Button>
        </AddStoreDialog>
      )}
    </div>
  );
}

interface StoreItemProps {
  store: ShopReturnToStoreStoresDto;
}

function StoreItem({ store }: StoreItemProps) {
  return (
    <UpdateStoreDialog store={store}>
      <LocationCardContainer>
        <LocationCardHeader>
          <LocationCardHeaderIcon icon={HomeIcon} />
          <LocationCardTitle>
            {store.name ?? store.address.companyName}{" "}
            {!store.selectableByUser && (
              <Badge variant="outline" size="xs">
                Hidden
              </Badge>
            )}
          </LocationCardTitle>
        </LocationCardHeader>
        <LocationCardContent>
          <LocationCardAddress>
            {store.address.address1}{" "}
            {store.address.address2 && `, ${store.address.address2} `}
            {store.address.city} {store.address.country}
            <StoreOpeningState openingHours={store.openingHours} />
          </LocationCardAddress>
          {store.embedMap && <LocationCardMapIcon />}
        </LocationCardContent>
      </LocationCardContainer>
    </UpdateStoreDialog>
  );
}
